import { useLayoutEffect, useRef } from 'react';
import { Icon } from '@/components/kit';

/**
 * A NOTE, TYPED IN PLACE — quoted, wrapping, with the pencil right after the
 * closing quote: “poor fixation — best effort, right eye” ✎.
 *
 * The board draws a note as running text, so the text itself is the editable
 * thing (plain text only; Enter never adds a line). Saved on blur, so a note is
 * never lost between typing it and pressing Send.
 */
export function NoteField({
  value,
  onChange,
  onCommit,
  label,
  placeholder,
  maxLength = 300,
}: {
  value: string;
  onChange: (next: string) => void;
  onCommit?: (value: string) => void;
  label: string;
  placeholder: string;
  maxLength?: number;
}) {
  const el = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (el.current && el.current.textContent !== value) el.current.textContent = value;
  }, [value]);
  return (
    <>
      {value ? '“' : null}
      <span
        ref={el}
        role="textbox"
        aria-label={label}
        tabIndex={0}
        contentEditable="plaintext-only"
        suppressContentEditableWarning
        data-placeholder={placeholder}
        className="tech-note"
        onInput={(e) => {
          const node = e.currentTarget;
          let text = (node.textContent ?? '').replace(/\n/g, ' ');
          if (text.length > maxLength) {
            text = text.slice(0, maxLength);
            node.textContent = text;
          }
          onChange(text);
        }}
        onBlur={(e) => onCommit?.((e.currentTarget.textContent ?? '').trim())}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          }
        }}
      />
      {value ? '”' : null} <Icon name="pencil" size="sm" className="mut" />
    </>
  );
}
